import { useTranslation } from "react-i18next";
import {
  getAttributeValueExtra,
  getServiceAttributeLabel,
  getServiceAttributeValueLabel,
  isVariantOptionAvailable,
  type ServiceVariant,
  type ServiceVariantAttributeGroup,
  type ServiceVariantSelections,
} from "../../utils/serviceVariants";

type Props = {
  variantAttributes: Record<string, ServiceVariantAttributeGroup>;
  variants: ServiceVariant[];
  selectableKeys: string[];
  selectedByAttribute: ServiceVariantSelections;
  onChange: (attributeKey: string, valueId: number) => void;
  className?: string;
};

const ServiceVariantSelector = ({
  variantAttributes,
  variants,
  selectableKeys,
  selectedByAttribute,
  onChange,
  className,
}: Props) => {
  const { t, i18n } = useTranslation();
  const groups = Object.entries(variantAttributes);

  if (groups.length === 0) return null;

  return (
    <div className={className}>
      {groups.map(([attributeKey, group]) => {
        const selectedValue = selectedByAttribute[attributeKey];
        return (
          <div key={attributeKey} className="ac-shop-detail__variant-group">
            <p className="ac-shop-detail__variant-title">{getServiceAttributeLabel(attributeKey, group.label, i18n.language)}</p>
            <div
              className="ac-shop-detail__variant-options"
              role="radiogroup"
              aria-label={getServiceAttributeLabel(attributeKey, group.label, i18n.language)}
            >
              {group.values.map((option) => {
                const selected = option.id === selectedValue;
                const extra = getAttributeValueExtra(option);
                const available = isVariantOptionAvailable(
                  variants,
                  selectableKeys,
                  selectedByAttribute,
                  attributeKey,
                  option.id
                );

                return (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-disabled={!available}
                    disabled={!available}
                    className={`ac-shop-detail__variant-option${
                      selected ? " is-selected" : ""
                    }${!available ? " is-disabled" : ""}`}
                    onClick={() => {
                      if (!available) return;
                      onChange(attributeKey, option.id);
                    }}
                  >
                    <span className="ac-shop-detail__variant-option-main">
                      <span className="ac-shop-detail__variant-option-label">
                        {getServiceAttributeValueLabel(attributeKey, option.label, i18n.language)}
                      </span>
                      {extra > 0 ? (
                        <span className="ac-shop-detail__variant-option-extra icon-saudi_riyal">
                          {t("services.detail.variantExtra", {
                            amount: extra,
                          })}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ServiceVariantSelector;
